/**
 * Client-side download helpers.
 *
 * There are two flavours of download in this app:
 *
 * 1. **Backend-produced files** (Word→PDF, JPG→PNG, image compressor, image
 *    resizer, YouTube thumbnails).  The backend stages the file and
 *    returns a `/api/tools/download/<id>` URL that streams the file with
 *    `Content-Disposition: attachment`.  For these, the frontend simply
 *    calls `downloadFromUrl(url)` (a thin wrapper around
 *    `window.location.href = url`), which triggers the browser's native
 *    download flow — the most reliable approach on every platform,
 *    including iOS Safari.
 *
 * 2. **Client-generated files** (QR code PNG/SVG, Lorem Ipsum .txt).  The
 *    content only exists as a Blob or a data URL in the browser, so we
 *    fall back to the classic "hidden anchor + `URL.createObjectURL`"
 *    trick via `downloadBlob(blob, filename)`.
 *
 * Historical bugs this file has already fixed:
 *   • The anchor click MUST happen inside the same synchronous stack as
 *     the user's own click handler, or iOS/Safari drops it as a
 *     "programmatic navigation" and shows nothing.  We therefore do all
 *     the DOM juggling synchronously — no `setTimeout`, no `await` — the
 *     one exception being `URL.revokeObjectURL`, which we defer so the
 *     browser has time to actually start the download before we yank the
 *     blob URL out from under it.
 *   • Older iOS Safari (up to ~16.4) refuses to download from a
 *     `blob:` URL entirely.  We detect it and fall back to converting the
 *     blob into a data URL via `FileReader.readAsDataURL`, which iOS is
 *     willing to open as a normal download.
 */

/**
 * Approximate detection of iOS Safari.
 *
 * We can't use feature detection here (there's no API that says "will this
 * platform refuse blob: downloads?"), so we fall back to UA sniffing.  We
 * accept a few false positives — iPadOS on desktop mode still needs the
 * data-URL fallback, and any WebKit engine on iOS has the same limitation
 * — but false negatives are the dangerous case, so we err on the side of
 * flagging more browsers than fewer.
 */
function isIosSafari() {
    if (typeof navigator === 'undefined') return false;
    const ua = navigator.userAgent || '';
    // iPhone / iPod / iPad classic
    const iOS = /iP(hone|od|ad)/i.test(ua);
    // iPadOS 13+ reports itself as Mac but exposes touch — that flag is
    // the only reliable way to tell an iPad from a real Mac.
    const iPadOS = /Macintosh/.test(ua)
        && typeof document !== 'undefined'
        && 'ontouchend' in document;
    if (!(iOS || iPadOS)) return false;
    // Chrome-on-iOS ("CriOS") is still WebKit under the hood and has the
    // same limitation, so we don't exclude it.
    return true;
}

/**
 * Trigger a download from an already-existing URL (backend endpoint,
 * remote CDN, etc.).  Simply sets `window.location.href` — the browser
 * follows the URL, sees `Content-Disposition: attachment`, and downloads
 * the file natively.  Works on iOS Safari.
 *
 * `filename` is accepted so the call-site can be forward-compatible if we
 * later switch to an `<a href download="…">` element, but it's not
 * actually needed today because the backend already dictates the filename
 * via Content-Disposition.
 */
export function downloadFromUrl(url, filename) {
    if (!url) return;
    // Keep the "filename" parameter alive for the linter — it's part of
    // the public contract and will be used if we swap this out for an
    // <a> element in the future.
    void filename;
    if (typeof window !== 'undefined') {
        window.location.href = url;
    }
}

/**
 * Download a Blob as a file, prompting the browser's save-as flow.
 *
 * MUST be called synchronously inside the user's click handler — see the
 * file-level comment above.
 *
 * On iOS Safari (which refuses blob: URLs for downloads) we transparently
 * fall back to a data URL via `FileReader.readAsDataURL`.  The fallback
 * is asynchronous, but the anchor element itself is created + clicked
 * inside the same microtask that the FileReader dispatches, so from the
 * browser's perspective the "click" still originates from a legitimate
 * user gesture that hasn't been interrupted by a full task boundary.
 */
export function downloadBlob(blob, filename) {
    if (!blob) return;
    const safeName = filename || 'download';

    if (isIosSafari()) {
        // iOS Safari path — convert to data URL first.  We don't have a
        // synchronous way to do this, so we open the download in the
        // continuation of the FileReader callback.  Modern iOS honours
        // this because the click still originated from the user gesture.
        const reader = new FileReader();
        reader.onload = () => {
            const dataUrl = reader.result;
            _triggerAnchorDownload(dataUrl, safeName, /* isBlobUrl */ false);
        };
        reader.onerror = () => {
            // Absolute last resort: try the blob URL anyway. It won't
            // work on older iOS, but on iOS 17+ it does — and there's no
            // better option here.
            const url = URL.createObjectURL(blob);
            _triggerAnchorDownload(url, safeName, /* isBlobUrl */ true);
        };
        reader.readAsDataURL(blob);
        return;
    }

    // Standard path — blob: URL + anchor click, revoke after a delay.
    const url = URL.createObjectURL(blob);
    _triggerAnchorDownload(url, safeName, /* isBlobUrl */ true);
}

/**
 * Internal: build an <a>, click it, and (for blob URLs) schedule cleanup.
 *
 * Kept as a single function so both the standard path and the iOS
 * fallback share exactly the same anchor semantics.
 *
 * The click MUST happen synchronously inside this function — no awaits
 * between appendChild and .click() — because iOS specifically detects
 * "click that is one microtask past a user gesture" and refuses it.
 */
function _triggerAnchorDownload(href, filename, isBlobUrl) {
    const a = document.createElement('a');
    a.href = href;
    a.download = filename;
    // rel=noopener protects against edge cases where the browser
    // interprets the anchor as a navigation instead of a download.
    a.rel = 'noopener';
    // Firefox needs the anchor to be in the document tree.
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    if (isBlobUrl) {
        // Revoke *late* — the browser may still be reading from the blob
        // URL when we return, and revoking too soon aborts the download
        // (a common gotcha, especially on Firefox with large files and
        // on slow Android devices).  10 seconds is well beyond what any
        // browser needs to start a download.  There's no harm in keeping
        // the URL alive that long — a single 25 MB blob URL costs a few
        // dozen bytes of bookkeeping until it's revoked.
        setTimeout(() => {
            try {
                URL.revokeObjectURL(href);
            } catch (_) { /* older Safari sometimes throws — ignore */ }
        }, 10_000);
    }
}

/**
 * Small utility used by the QR page: convert a `data:` URL into a Blob
 * so it can flow through `downloadBlob()` and hit the same iOS fallback.
 *
 * Handles both base64 and URL-encoded payloads.
 */
export function dataUrlToBlob(dataUrl) {
    if (!dataUrl || typeof dataUrl !== 'string') return null;
    const commaIdx = dataUrl.indexOf(',');
    if (commaIdx < 0) return null;
    const meta = dataUrl.slice(5, commaIdx); // "image/png;base64" or "image/svg+xml"
    const payload = dataUrl.slice(commaIdx + 1);
    const [mime, ...params] = meta.split(';');
    const isBase64 = params.includes('base64');
    let bytes;
    if (isBase64) {
        const bin = atob(payload);
        bytes = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    } else {
        const decoded = decodeURIComponent(payload);
        bytes = new TextEncoder().encode(decoded);
    }
    return new Blob([bytes], { type: mime || 'application/octet-stream' });
}

export function humanBytes(n) {
    if (n == null || Number.isNaN(n)) return '–';
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

export function replaceExt(filename, newExt) {
    if (!filename) return `file.${newExt}`;
    const dot = filename.lastIndexOf('.');
    if (dot <= 0) return `${filename}.${newExt}`;
    return `${filename.slice(0, dot)}.${newExt}`;
}
